import { useState } from 'react';
import { MAX_ROOM_NAME_LENGTH } from '@sokdaq/protocol';
import { useChatStore } from '../store/chat.js';

/**
 * 방장 전용 설정.
 *
 * 방장이 아니면 아예 그리지 않는다. 눌러도 서버가 거절할 버튼을 보여주면
 * 고장난 것처럼 보인다 — 서버는 물론 다시 확인하지만, 그건 화면을 우회해
 * 프레임을 직접 보내는 경우를 막기 위한 것이다.
 */
export function OwnerControls() {
  const room = useChatStore((s) => s.room);
  const you = useChatStore((s) => s.you);
  const members = useChatStore((s) => s.members);
  const updateRoom = useChatStore((s) => s.updateRoom);
  const rotateCode = useChatStore((s) => s.rotateCode);
  const kick = useChatStore((s) => s.kick);

  const [draftName, setDraftName] = useState<string | null>(null);

  if (room === null || you === null || you.role !== 'owner') return null;

  const others = members.filter((m) => m.userId !== you.userId);

  return (
    <section className="mb-3 border-t border-neutral-800 pt-3 last:mb-0">
      <h2 className="mb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
        방 관리
      </h2>

      {/* 이름 바꾸기 — 편집 중에만 초안을 들고, 저장하면 서버 값으로 돌아간다. */}
      <form
        className="mb-2 flex gap-1"
        onSubmit={(event) => {
          event.preventDefault();
          const next = (draftName ?? '').trim();
          if (next !== '' && next !== room.name) updateRoom({ name: next });
          setDraftName(null);
        }}
      >
        <input
          value={draftName ?? room.name}
          onChange={(event) => setDraftName(event.target.value)}
          maxLength={MAX_ROOM_NAME_LENGTH}
          aria-label="방 이름"
          className="min-w-0 flex-1 rounded border border-neutral-700 bg-neutral-800 px-2 py-1 text-xs outline-none focus:border-neutral-500"
        />
        <button
          type="submit"
          disabled={draftName === null || draftName.trim() === ''}
          className="tap shrink-0 rounded bg-neutral-700 px-2 text-xs text-neutral-100 disabled:opacity-40"
        >
          저장
        </button>
      </form>

      <button
        type="button"
        onClick={() => updateRoom({ locked: !room.locked })}
        aria-pressed={room.locked}
        className="tap mb-2 w-full rounded px-2 py-1 text-left text-xs text-neutral-300 hover:bg-neutral-800"
      >
        <span className={room.locked ? 'text-amber-400' : 'text-neutral-600'}>●</span>{' '}
        {room.locked ? '잠김 — 새로 들어올 수 없음' : '열림 — 코드가 있으면 들어올 수 있음'}
      </button>

      <RotateCode onRotate={rotateCode} />

      {others.length > 0 && (
        <div className="mt-3">
          <h3 className="mb-1 text-[10px] text-neutral-500">사람 ({others.length})</h3>
          <ul className="flex flex-col gap-0.5">
            {others.map((member) => (
              <li key={member.userId} className="flex items-center gap-2">
                <span className={member.online ? 'text-emerald-400' : 'text-neutral-700'}>●</span>
                <span className="min-w-0 flex-1 truncate text-xs text-neutral-300">
                  {member.nickname}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`${member.nickname} 님을 내보낼까요? 다시 들어올 수 없습니다.`)) {
                      kick(member.userId);
                    }
                  }}
                  className="tap shrink-0 rounded px-1.5 text-[10px] text-neutral-500 hover:text-red-400"
                >
                  내보내기
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * 초대코드 회전.
 *
 * 되돌릴 수 없고, 지금 접속해 있지 않은 사람은 새 코드를 못 받는다.
 * 한 번 더 묻는 이유가 그것이다 — 실수로 누르면 팀 절반이 못 들어온다.
 */
function RotateCode({ onRotate }: { onRotate: () => void }) {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="tap w-full rounded px-2 py-1 text-left text-xs text-neutral-400 hover:bg-neutral-800"
      >
        초대코드 새로 만들기
      </button>
    );
  }

  return (
    <div className="rounded border border-amber-900/60 bg-amber-950/30 p-2">
      <p className="mb-1.5 text-[10px] leading-relaxed text-amber-200">
        지금 코드는 즉시 못 쓰게 됩니다. 접속 중인 사람은 그대로 남지만, 지금 자리에 없는
        사람에게는 새 코드를 다시 알려줘야 합니다.
      </p>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => {
            onRotate();
            setConfirming(false);
          }}
          className="tap flex-1 rounded bg-amber-700 px-2 py-1 text-xs text-white"
        >
          바꾸기
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="tap flex-1 rounded px-2 py-1 text-xs text-neutral-400 hover:bg-neutral-800"
        >
          취소
        </button>
      </div>
    </div>
  );
}
